import {
  InteropConsultation,
  InteropLogPayload,
  InteropPatient,
  TInteropPatientVisit,
} from './interop-log.type'
import { CurecallCopyButton, CurecallList, ECurecallSize } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'src/utils/tw-merge.util'
import { formatAllDatesWithHoursInObject } from 'src/utils/date-formatter.util'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'

export type TInteropLogPayloadComponentProps = {
  interopLogPayload: InteropLogPayload | undefined
}

function convertObjectPropToString(object: Record<string, unknown>, key: string) {
  return typeof object[key] === 'object' ? JSON.stringify(object[key], null, 2) : String(object[key])
}

function InteropLogDetailComponent({
  title,
  interopData,
  copyPrefix,
  backgroundColor,
}: Readonly<{
  title: string
  interopData: InteropConsultation | InteropPatient | TInteropPatientVisit | string[]
  /**
   * This value will be set as the unique key in the object inserted in user's
   * clipboard when clicking the copy button.
   * It is useful for mimicking data received by InteropEngine, thus allowing
   * for debugging InteropMappings conditions on InteropLog Data.
   * To do this, go to https://try.jsonata.org/, on the left panel (JSON input)
   * paste the "consultation" data, on the top-right panel (condition) paste the
   * InteropMapping condition. You have the condition result on the bottom-right
   * panel.
   */
  copyPrefix: string
  backgroundColor?: string
}>) {
  const { t } = useTranslation()
  const { displayTimezone } = useTimezonePreference()
  const formattedData = formatAllDatesWithHoursInObject(t, interopData, displayTimezone)
  return (
    <div
      className={twMerge(
        backgroundColor ?? 'c-bg-amber-50',
        'c-rounded-lg c-grow c-p-1 c-border c-border-grey-500 c-flex-1',
      )}
    >
      <div className="c-flex c-items-center c-justify-between">
        <h5 className="c-text-center c-grow">{title} </h5>
        <CurecallCopyButton
          ariaLabelText={t('components.patient.interop-logs.detail.copy-data', { prefix: copyPrefix })}
          data={JSON.stringify({
            [copyPrefix]: formattedData,
          })}
          size={ECurecallSize.SMALL}
        />
      </div>
      <CurecallList
        headersData={[
          { key: 'key', label: t('components.patient.interop-logs.detail.key-column'), isSorted: true },
          { key: 'value', label: t('components.patient.interop-logs.detail.value-column'), isSorted: true },
        ]}
        rowsData={Object.keys(formattedData).map((key) => ({
          key,
          value: convertObjectPropToString(formattedData, key),
        }))}
      ></CurecallList>
    </div>
  )
}

export function InteropLogPayloadDetailComponent({
  interopLogPayload,
}: Readonly<TInteropLogPayloadComponentProps>) {
  const { t } = useTranslation()
  if (!interopLogPayload) {
    return <div>{t('components.patient.interop-logs.no-data')}</div>
  }
  const patient = interopLogPayload.patient
  const consultation = interopLogPayload.consultation
  const visit = interopLogPayload.visit
  const preAdmission = interopLogPayload.preAdmission
  const consultationsToRemove = interopLogPayload.consultationsToRemove
  return (
    <div className="c-flex c-gap-2 c-m-1 c-flex-wrap">
      <InteropLogDetailComponent
        title={t('components.patient.interop-logs.detail.patient')}
        interopData={patient}
        copyPrefix={'patient'}
        backgroundColor={'c-bg-gray-50'}
      />
      {consultation && (
        <InteropLogDetailComponent
          title={t('components.patient.interop-logs.detail.consultation')}
          interopData={consultation}
          copyPrefix={'consultation'}
          backgroundColor={'c-bg-amber-50'}
        />
      )}
      {visit && (
        <InteropLogDetailComponent
          title={t('components.patient.interop-logs.detail.visit')}
          interopData={visit}
          copyPrefix={'visit'}
          backgroundColor={'c-bg-green-50'}
        />
      )}
      {preAdmission && (
        <InteropLogDetailComponent
          title={t('components.patient.interop-logs.detail.preadmission')}
          interopData={preAdmission}
          copyPrefix={'preAdmission'}
          backgroundColor={'c-bg-blue-50'}
        />
      )}
      {consultationsToRemove && (
        <InteropLogDetailComponent
          title={t('components.patient.interop-logs.detail.consultation-to-remove')}
          interopData={consultationsToRemove}
          copyPrefix={'consultationToRemove'}
          backgroundColor={'c-bg-red-50'}
        />
      )}
    </div>
  )
}
